import { DEFAULT_PAGE_SIZE } from '@/constants';
import { usePaginatedCrudModel } from '@/models/shared/usePaginatedCrudModel';
import {
  createUser,
  deleteUser,
  fetchManageableRoles,
  fetchUsers,
  getCurrentUser,
  reactivateUser,
  updateUser,
} from '@/services/user/userService';
import type {
  User,
  UserListParams,
  UserListResult,
  UserPayload,
  UserRole,
} from '@/types/user';
import { logger } from '@/utils/logger';
import { useCallback, useState } from 'react';

type FetchCurrentUserOptions = {
  rethrowOnError?: boolean;
  useGlobalErrorHandler?: boolean;
  clearOnError?: boolean;
};

const useUserModel = () => {
  const [currentUser, setCurrentUser] = useState<User | undefined>();
  const [manageableRoles, setManageableRoles] = useState<UserRole[]>([]);
  const [manageableRolesLoading, setManageableRolesLoading] = useState(false);

  const {
    items: users,
    loading,
    pagination,
    loadItems: loadUsers,
    create,
    update,
    remove,
  } = usePaginatedCrudModel<
    User,
    UserListParams,
    UserPayload,
    UserPayload,
    UserListResult
  >({
    defaultPageSize: DEFAULT_PAGE_SIZE,
    initialQuery: {},
    loadList: fetchUsers,
    buildQuery: (params, lastQuery, paginationState) => ({
      page: params.page ?? paginationState.current,
      size:
        params.size ??
        params.page_size ??
        params.pageSize ??
        paginationState.pageSize,
      email: 'email' in params ? params.email : lastQuery.email,
      role: 'role' in params ? params.role : lastQuery.role,
      status: 'status' in params ? params.status : lastQuery.status,
    }),
    createItem: createUser,
    updateItem: updateUser,
    deleteItem: deleteUser,
  });

  const fetchCurrentUser = useCallback(
    async (options: FetchCurrentUserOptions = {}) => {
      const {
        rethrowOnError = false,
        useGlobalErrorHandler = true,
        clearOnError = true,
      } = options;

      try {
        const user = await getCurrentUser({ useGlobalErrorHandler });
        setCurrentUser(user);
        return user;
      } catch (error) {
        if (clearOnError) {
          setCurrentUser(undefined);
        }
        if (rethrowOnError) {
          throw error;
        }
        return undefined;
      }
    },
    [],
  );

  const clearCurrentUser = useCallback(() => {
    setCurrentUser(undefined);
  }, []);

  const loadManageableRoles = useCallback(async () => {
    setManageableRolesLoading(true);
    try {
      const roles = await fetchManageableRoles();
      setManageableRoles(Array.isArray(roles) ? roles : []);
      return roles;
    } catch (error) {
      logger.error('Error loading manageable roles', {
        scope: 'user_model',
        error,
      });
      setManageableRoles([]);
      return [];
    } finally {
      setManageableRolesLoading(false);
    }
  }, []);

  const reactivate = useCallback(async (id: number) => {
    return reactivateUser(id);
  }, []);

  return {
    currentUser,
    setCurrentUser,
    fetchCurrentUser,
    clearCurrentUser,
    users,
    loading,
    pagination,
    manageableRoles,
    manageableRolesLoading,
    loadUsers,
    loadManageableRoles,
    create,
    update,
    remove,
    reactivate,
  };
};

export default useUserModel;
